import React, { useState, useRef, useEffect } from 'react';
import '../css/countrySelect.css';
import { ChevronIcon, SearchIcon } from '../../../../../assets-module/asset-configuration/icon/iconExporter/iconExporter';

export interface CountryOption {
  code: string;
  name: string;
  dialCode: string;
  flag: string;
}

export interface CountrySelectProps {
  id?: string;
  name?: string;
  value?: string;
  placeholder?: string;
  label?: string;
  options: CountryOption[];
  onChange?: (country: CountryOption) => void;
  disabled?: boolean;
  className?: string;
  searchable?: boolean;
  required?: boolean;
  error?: boolean;
  errorText?: string;
  warning?: boolean;
}

const CountrySelect: React.FC<CountrySelectProps> = ({
  id,
  name,
  value,
  placeholder,
  label,
  options,
  onChange,
  disabled = false,
  className = '',
  searchable = true,
  required = false,
  error = false,
  errorText,
  warning = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find(opt => opt.code === value);
  const displayLabel = label || placeholder;
  const hasValue = value && value.length > 0;
  const showFloatingLabel = isOpen || hasValue;

  const hasAsterisk = displayLabel?.includes('*');
  const labelText = hasAsterisk ? displayLabel?.replace(' *', '').replace('*', '') : displayLabel;

  const filteredOptions = searchable
    ? options.filter(option =>
      option.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      option.dialCode.includes(searchTerm)
    )
    : options;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const handleSelect = (option: CountryOption) => {
    if (onChange) {
      onChange(option);
    }
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const renderLabel = () => {
    /* istanbul ignore next */
    if (!displayLabel) return null;
    return (
      <>
        {labelText}
        {hasAsterisk && <span className="scl--web-component-country-select-required-asterisk"> *</span>}
      </>
    );
  };

  return (
    <div
      ref={containerRef}
      className={`scl--web-component-country-select-container ${className}`}
    >
      <div //NOSONAR
        className={`scl--web-component-country-select-input-plate ${error ? 'error' : ''} ${warning ? 'warning' : ''} ${disabled ? 'disabled' : ''} ${isOpen ? 'open' : ''}`}
        onClick={handleToggle}
      >
        {displayLabel && (
          <label className={`scl--web-component-country-select-label ${showFloatingLabel ? 'floating' : ''}`}>
            {renderLabel()}
          </label>
        )}

        <div className="scl--web-component-country-select-input-wrapper">
          <div className="scl--web-component-country-select-value-wrapper">
            <div className="scl--web-component-country-select-text-wrapper">
              {hasValue && selectedOption && (
                <span className="scl--web-component-country-select-text">
                  <span className="scl--web-component-country-select-selected-flag">
                    <img src={selectedOption?.flag} alt="" />
                  </span>
                  <span className="scl--web-component-country-select-dial-code">
                    {selectedOption.dialCode}
                  </span>
                </span>
              )}
            </div>

            <ChevronIcon className="scl--web-component-country-select-chevron-icon" />
          </div>
        </div>
      </div>

      <div className={`scl--web-component-country-select-dropdown ${isOpen ? 'open' : ''}`}>
        {searchable && (
          <div className="scl--web-component-country-select-search">
            <SearchIcon className="scl--web-component-country-select-search-icon" />
            <input
              type="text"
              placeholder="Search"
              value={searchTerm}
              onChange={handleSearchChange}
              className="scl--web-component-country-select-search-input"
              onClick={(e) => e.stopPropagation()}
              autoFocus={isOpen}
              autoComplete='off'
            />
          </div>
        )}

        <div className="scl--web-component-country-select-options">
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option) => (
              <div //NOSONAR
                key={option.code}
                className={`scl--web-component-country-select-option ${value === option.code ? 'selected' : ''}`}
                onClick={() => handleSelect(option)}
              >
                <span className="scl--web-component-country-select-option-flag">
                  <img src={option?.flag} alt="" />
                </span>
                <span className="scl--web-component-country-select-option-label">
                  {option.name} {option.dialCode}
                </span>
              </div>
            ))
          ) : (
            <div className="scl--web-component-country-select-no-options">
              No countries found
            </div>
          )}
        </div>
      </div>

      {error && errorText && (
        <div className="scl--web-component-country-select-error-text">
          {errorText}
        </div>
      )}

      {/* Hidden select for form submission */}
      <select
        id={id}
        name={name}
        value={value}
        onChange={() => { }}
        required={required}
        disabled={disabled}
        style={{ display: 'none' }}
      >
        <option value="">{placeholder}</option>
        {options.map((option) => (
          <option key={option.code} value={option.code}>
            {option.name} {option.dialCode}
          </option>
        ))}
      </select>
    </div>
  );
};

export default CountrySelect;